import { ref } from "vue";
import { defineStore } from "pinia";
import type { BeforeInstallPromptEvent } from "@/types/events";

export const usePwaStore = defineStore("pwa", () => {
	// The PWA install event, which is captired and stored here so that we can
	// control the install process.
	const event = ref<BeforeInstallPromptEvent | undefined>(undefined);

	// If the service worker detects a new version of the app exists, this flag
	// is set to true and we can use it to prompt the user to update the app.
	const showRefresh = ref(false);

	// If the service worker detects that the app is ready to work offline (this
	// should mean that appropriate data is cached), this flag gets set to true
	const isOfflineReady = ref(false);

	// If a chatty update fails, signal a full refresh is required
	const requireSync = ref(false);

	// If the user manually sets the app to offline
	const manuallyOnline = ref(true);

	// If there is an update in progress
	const updateInProgress = ref(false);
	const lastUpdateTook = ref("");
	const timerTime = ref(0);
	const updateItemId = ref("");

	// flag to turn off v2 features
	// const version = 2;
	const version = import.meta.env.VITE_APP_VERSION;

	// The function to update the app.
	const updateSW = ref<(reloadPage?: boolean | undefined) => Promise<void>>(() => {
		return Promise.reject();
	});

	// A browser can be online (eg connected to WiFi) but not able to use the
	// web (eg WiFi has dead gateway). We use this state to track if the user
	// is both online AND connected.
	const onlineAndConnected = ref(true);

	// An event handler for when the user goes offline.
	function offline () {
		onlineAndConnected.value = false;
	}

	// An event handler for when the user goes offline.
	function online () {
		getOnlineAndConnected();
	}

	// Verify is the browser is both online (has a network connection) and
	// connected (the network connection works)
	function getOnlineAndConnected () {
		fetch("/", { method: "HEAD" }).then((response) => {
			onlineAndConnected.value = navigator.onLine && response.status === 200;
		}).catch(() => {
			onlineAndConnected.value = false;
		});
	}

	// a somewhat verbose approach to iOS detection
	function isThisDeviceRunningiOS () {
		// TODO
		if (["iPad Simulator", "iPhone Simulator", "iPod Simulator", "iPad", "iPhone", "iPod"].includes(navigator.platform)) {
			return true;
		}
		else if (navigator.userAgent.includes("Mac") && "ontouchend" in document) {
			// iPad on iOS 13
			return true;
		}
		else {
			return false;
		}
	}

	function manuallyOffline () {
		return !manuallyOnline.value;
	}
	function setManuallyOnline (val: boolean) {
		manuallyOnline.value = val;
	}

	function startUpdate () {
		updateItemId.value = "";
		updateInProgress.value = true;
		timerTime.value = performance.now();
	}
	function endUpdate (id: string | null, silent = false) {
		lastUpdateTook.value = ((performance.now() - timerTime.value) / 1000).toFixed(2);
		updateInProgress.value = false;
		// if (Number(lastUpdateTook.value) > 4 && !silent) alert("Updates are taking a longer time than usual (" + lastUpdateTook.value + "s), consider using the app in offline mode. Don't forget to turn online mode back on if you reach an area of better signal");
		if (id) updateItemId.value = id;

	}
	const runningCalculations = ref(false);
	function startRunningCalculations () { runningCalculations.value = true; }
	function stopRunningCalculations () { runningCalculations.value = false; }

	const lastKnownPosition = ref({ longitude: 0, latitude: 0, accuracy: 5 });

	function confirmManualOfflineSwitchToggle (): boolean {
		const confirmed = window.confirm(
			(manuallyOnline.value ?
				"You are switching to offline mode. Your device will still receive GPS signal " +
				"where available and mobile data outside of the WCA app.\r\r" +
				"Whilst offline, no data will sync to the server until you switch the app " +
				"back to online mode. It is best practice to wait until you have good mobile " +
				"signal before switching back to online mode." :
				"You are switching to online mode. Edits to data on this device will be treated as the " +
				"ground truth and overwrite any edits to the same existing data made from another device. " +
				"New data created on another device will still be kept.") +
				"\r\rDo you want to continue?"
		);
		console.log("confirmManualOfflineSwitchToggle: " + confirmed);
		return confirmed;

	}

	return {
		event,
		showRefresh,
		isOfflineReady,
		requireSync,
		manuallyOnline,
		manuallyOffline,
		setManuallyOnline,
		updateSW,
		getOnlineAndConnected,
		onlineAndConnected,
		online, offline,
		updateInProgress,
		startUpdate, endUpdate,
		lastUpdateTook,
		updateItemId,
		runningCalculations,
		startRunningCalculations,
		stopRunningCalculations,
		confirmManualOfflineSwitchToggle,
		lastKnownPosition,
		version };
}, {
	persist: {
		// Ensure none of this store is persisted. We don't want stale updateSW functions
		// that don't match the current service worker.
		// requireSync must persist too: it's the only signal that tells the app
		// data created/edited offline still needs to reach the server. If it
		// resets to false on reload (e.g. tab closed while offline, reopened
		// later with signal), the pending records sit in the persisted auth
		// store's user object forever and are never synced.
		paths: ["manuallyOnline", "isOfflineReady", "requireSync"]
	}
});
